import React from "react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useFormFields } from "libs/fields";
import { onError } from "libs/error";
import { Auth } from "aws-amplify";
import { PasswordValidation, Validate } from "components/PasswordValidation";
import { SubmitButton, EmailInput, PasswordInput } from "components/FormElements";
import Header from "components/Header";
import SubHeader from "components/SubHeader";
import Footer from "components/Footer";

export default function Signup() {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<JSX.Element>();

  const [fields, handleFieldChange] = useFormFields({
    email: "",
    password: ""
  });

  useEffect(() => {
    document.title = t("local.signup.title.page") + " - " + t("global.system.name");
  }, []);

  function validateForm() {
    return Validate(fields.password).isValid && fields.email.length > 0;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    setIsLoading(true);

    try {
      await Auth.signUp({
        username: fields.email,
        password: fields.password,
        attributes: {
          email: fields.email,
          "custom:preferred_language": i18n.resolvedLanguage
        },
        autoSignIn: {
          enabled: true
        }
      });
      setIsLoading(false);
      navigate("/signup/verification", { state: { username: fields.email } });
    } catch (error: unknown) {
      const e = error as Error;
      const errorHandler = onError(e);
      setErrorMessage(errorHandler);
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <SubHeader heading={t("local.signup.subtitle.module").toString()} />
      <main className="flex-grow bg-ct-lightgrey px-4 md:px-0">
        <h2 className="text-3xl text-black text-center pt-20 mb-2.5">{t("local.signup.title.main")}</h2>
        <p className="text-base text-black text-center mb-6">{t("local.signup.subtitle.main")}</p>
        <form className="container mx-auto max-w-md flex flex-col justify-center space-y-4 pb-20" onSubmit={handleSubmit}>
          <EmailInput handleChange={handleFieldChange} value={fields.email} label={t("global.label.email")} />
          <PasswordInput
            handleChange={handleFieldChange}
            handleShow={() => setShowPassword(!showPassword)}
            visible={showPassword}
            value={fields.password}
            label={t("global.label.enterpassword")}
          />
          <PasswordValidation password={fields.password} />
          <p className="text-sm uppercase">
            {t("local.signup.label.terms1", {
              signup: t("global.button.signup")
            })}
            <a
              href={process.env.REACT_APP_URL_PAGE_TERMSOFSERVICE}
              className="transition text-ct-yellow hover:text-black"
              target="_blank"
              rel="noreferrer"
            >
              {t("global.link.terms")}{" "}
            </a>
            {t("local.signup.label.terms2")}
            <a
              href={process.env.REACT_APP_URL_PAGE_PRIVACYPOLICY}
              className="transition text-ct-yellow hover:text-black"
              target="_blank"
              rel="noreferrer"
            >
              {t("global.link.privacy")}
            </a>
          </p>
          <SubmitButton disabled={!validateForm() || isLoading} loading={isLoading} label={t("global.button.signup")} />
          {errorMessage}
        </form>
      </main>
      <Footer />
    </div>
  );
}
